import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAudioContextCtor } from '@/lib/voice/audioContext';
import { getVoicePreferences } from '@/lib/voicePreferences';

export interface DeviceOption {
  deviceId: string;
  label: string;
}

type VoicePrefs = ReturnType<typeof getVoicePreferences>;

/**
 * Mic-test + audio-device enumeration for the "Голос" settings tab.
 *
 * Owns the getUserMedia + Web Audio pipeline:
 *   stream → MediaStreamAudioSourceNode → AnalyserNode → level meter
 *   stream → GainNode → AudioDestinationNode (optional self-listen)
 *
 * Tearing down the test stream is critical — an open audio track keeps the
 * browser mic indicator red and drains battery. Cleanup runs on both paths
 * (component unmount and stop-by-user) through the same stopTest() helper.
 *
 * Device labels + IDs are masked by the browser until mic permission has been
 * granted once for the origin; on mount we do a silent permission *probe* (only
 * when permissions.query reports 'granted' — never an auto-prompt) so the
 * pickers are useful immediately.
 *
 * `prefs` is the live voice-preferences mirror; the test reacts to inputDeviceId
 * / echoCancellation changes mid-run (applyConstraints hot-swap, or a full
 * re-acquire on a device switch).
 */
export function useMicTest(prefs: VoicePrefs) {
  const { t } = useTranslation();
  const [inputDevices, setInputDevices] = useState<DeviceOption[]>([]);
  const [outputDevices, setOutputDevices] = useState<DeviceOption[]>([]);
  const [testing, setTesting] = useState(false);
  const [listenSelf, setListenSelf] = useState(false);
  const [level, setLevel] = useState(0); // 0..1
  const [permError, setPermError] = useState<string | null>(null);
  // Waiting on getUserMedia (the permission prompt can sit open for a while):
  // the start button is disabled so a second click can't open a second mic.
  const [acquiring, setAcquiring] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const rafRef = useRef<number | null>(null);
  // Bumped by every start and stop: a getUserMedia that resolves after a
  // newer start/stop (or after unmount — settings closed mid-prompt) belongs
  // to a test nobody is running any more, and its tracks are stopped at once.
  const generationRef = useRef(0);
  const acquiringRef = useRef(false);
  // The input the running stream was opened on ('' = system default).
  const openedDeviceRef = useRef('');

  // Plugging in / removing a headset changes the list while the tab is open.
  useEffect(() => {
    const devices = navigator.mediaDevices;
    if (!devices?.addEventListener) return;
    const onChange = () => void enumerateAndSet();
    devices.addEventListener('devicechange', onChange);
    return () => devices.removeEventListener('devicechange', onChange);
    // enumerateAndSet only touches stable setters + the stable `t`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enumerate devices on mount. The browser hides device labels + IDs until mic
  // permission has been granted at least once for this origin — so the dropdown
  // would show only "По умолчанию" until the user hits "Проверить микрофон". To
  // make the picker useful immediately, we do a silent permission probe: if
  // `permissions.query` reports 'granted' (user has used voice channels before,
  // or tested earlier), we briefly acquire + release a stream just to populate
  // labels.
  //
  // We never auto-prompt — `permissions.query` returns 'prompt' on a fresh load,
  // and in that branch we just enumerate what we can. The user will see real
  // devices the first time they click "Проверить".
  useEffect(() => {
    (async () => {
      try {
        let granted = false;
        try {
          const status = await navigator.permissions.query({
            // Firefox/Safari may not support 'microphone' here; the
            // catch falls through to a label-less enumerate.
            name: 'microphone' as PermissionName,
          });
          granted = status.state === 'granted';
        } catch {
          /* not supported, skip silent probe */
        }
        if (granted) {
          const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
          // Release immediately — we only needed the side effect of
          // unmasking device labels on subsequent enumerateDevices().
          for (const tr of probe.getTracks()) tr.stop();
        }
      } catch {
        /* permission revoked / no devices — fall through to enumerate */
      }
      void enumerateAndSet();
    })();
    // enumerateAndSet only touches stable setters + the stable `t`; the probe
    // must run exactly once on mount, so the empty dep array is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function enumerateAndSet() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setInputDevices(
        devices
          .filter((d) => d.kind === 'audioinput')
          .map((d, i) => ({
            deviceId: d.deviceId || 'default',
            label: d.label || t('prof.voice.micFallback', { num: i + 1 }),
          })),
      );
      setOutputDevices(
        devices
          .filter((d) => d.kind === 'audiooutput')
          .map((d, i) => ({
            deviceId: d.deviceId || 'default',
            label: d.label || t('prof.voice.speakerFallback', { num: i + 1 }),
          })),
      );
    } catch {
      /* enumerate can fail before getUserMedia — fine */
    }
  }

  // useCallback with [] is safe: the function body only touches refs and stable setters.
  const stopTest = useCallback(() => {
    // Also abandons a start still waiting on getUserMedia.
    generationRef.current += 1;
    acquiringRef.current = false;
    setAcquiring(false);
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        /* idempotent */
      }
      sourceRef.current = null;
    }
    if (gainRef.current) {
      try {
        gainRef.current.disconnect();
      } catch {
        /* idempotent */
      }
      gainRef.current = null;
    }
    analyserRef.current = null;
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    setLevel(0);
    setTesting(false);
  }, []);

  // Hard cleanup on unmount — closes the mic so the browser indicator
  // doesn't stay red after the modal is gone.
  useEffect(() => {
    return () => stopTest();
  }, [stopTest]);

  async function startTest() {
    if (acquiringRef.current) return;
    acquiringRef.current = true;
    setAcquiring(true);
    const generation = ++generationRef.current;
    const deviceId = selectedInput(prefs.inputDeviceId);
    setPermError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          // Browser NS/AGC are replaced by «Как в бункере» — keep them off so
          // the mic test reflects what the voice channel actually sends.
          noiseSuppression: false,
          echoCancellation: prefs.echoCancellation,
          autoGainControl: false,
        },
      });
      if (generation !== generationRef.current) {
        // Stopped, restarted or unmounted while the prompt was open.
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      acquiringRef.current = false;
      setAcquiring(false);
      streamRef.current = stream;
      openedDeviceRef.current = deviceId;
      // After grant, labels are populated — refresh device list.
      void enumerateAndSet();

      const Ctor = getAudioContextCtor();
      const ctx = new Ctor();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      analyserRef.current = analyser;
      source.connect(analyser);

      // Loopback to speakers, gated by the listenSelf toggle. The gain
      // node lets us hot-swap volume without reconnecting nodes.
      const gain = ctx.createGain();
      gainRef.current = gain;
      gain.gain.value = listenSelf ? 0.6 : 0;
      source.connect(gain).connect(ctx.destination);

      setTesting(true);

      // Level meter loop — RMS over the latest sample window.
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(buf);
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) {
          const x = ((buf[i] ?? 128) - 128) / 128;
          sumSq += x * x;
        }
        const rms = Math.sqrt(sumSq / buf.length);
        // RMS of a clean speech sample is ~0.05-0.15; scale ×4 so the
        // meter doesn't look anemic at normal talk volume.
        setLevel(Math.min(1, rms * 4));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      // A newer start/stop owns the state now; this failure is old news.
      if (generation !== generationRef.current) return;
      const code = err instanceof Error ? err.name : 'UnknownError';
      setPermError(
        code === 'NotAllowedError'
          ? t('prof.voice.micDenied')
          : code === 'NotFoundError'
            ? t('prof.voice.micNotFound')
            : t('prof.voice.micFailed'),
      );
      stopTest();
    }
  }

  // Sync the listen-self toggle into the running gain node so it doesn't
  // require restarting the test.
  useEffect(() => {
    if (gainRef.current) {
      gainRef.current.gain.value = listenSelf ? 0.6 : 0;
    }
  }, [listenSelf]);

  // Apply filter / device changes to the LIVE test stream when prefs change
  // mid-test. Without this, toggling "Шумоподавление" while listening to
  // yourself in headphones does nothing — the constraints baked into the
  // original getUserMedia call don't update.
  //
  // applyConstraints can hot-swap echo/noise/AGC; deviceId changes usually need
  // a full re-acquire, which is why we restart on miss.
  useEffect(() => {
    if (!testing || !streamRef.current) return;
    const track = streamRef.current.getAudioTracks()[0];
    if (!track) return;
    // Another input — including back to «По умолчанию», which constraints
    // can't express (dropping `deviceId` just keeps the current one) — means
    // a new stream.
    if (selectedInput(prefs.inputDeviceId) !== openedDeviceRef.current) {
      stopTest();
      void startTest();
      return;
    }
    const next: MediaTrackConstraints = {
      noiseSuppression: false,
      echoCancellation: prefs.echoCancellation,
      autoGainControl: false,
    };
    void track.applyConstraints(next).catch(() => {
      // deviceId change or browser limitation — re-acquire the stream
      // from scratch so the new device + filter set actually takes hold.
      stopTest();
      void startTest();
    });
    // `startTest` reads the current prefs directly from the closure captured
    // at the time the effect ran. Since this effect re-runs whenever any pref
    // changes, the startTest closure is always at most one render stale — an
    // acceptable tradeoff to avoid making startTest a useCallback with the same
    // pref deps (which would not reduce re-runs but would add complexity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.inputDeviceId, prefs.echoCancellation, stopTest]);

  return {
    inputDevices,
    outputDevices,
    testing,
    listenSelf,
    setListenSelf,
    level,
    permError,
    acquiring,
    startTest,
    stopTest,
  };
}

/** The input a test should open: '' means the system default. */
function selectedInput(inputDeviceId: string | null | undefined): string {
  return inputDeviceId && inputDeviceId !== 'default' ? inputDeviceId : '';
}
